import { Button, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1098EInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1098-E instructions for Tax Year 2025"
          description="Learn how to fill out IRS Form 1098-E for Tax Year 2025 with step-by-step guidance from TaxZerone to accurately report student loan interest payments."
          keywords=""
          canonical="resources/information-returns/form-1098-e-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1098-E</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Essential information and instructions required for filing form 1098-E
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098einstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1098-e-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1098-E at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Form 1098-E, Student Loan Interest Statement{' '}
                        </strong>
                        , which lenders use to report student loan interest
                        received from borrowers. A lender must file{' '}
                        <Link
                          href="/information-returns/efile-irs-form-1098-e/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 1098-E
                        </Link>{' '}
                        with the IRS and provide a copy to the borrower if they
                        receive{' '}
                        <strong style={{ fontWeight: 600 }}>
                          more than $600 in student loan interest
                        </strong>{' '}
                        during the year.
                      </span>
                    </li>

                    <li>
                      <span>
                        Borrower copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}>February 2</strong>,
                        the IRS filing deadline is{' '}
                        <strong style={{ fontWeight: 600 }}>March 2</strong>,
                        for paper filings and{' '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>,
                        for electronic filings.
                      </span>
                    </li>

                    <li>
                      <span>
                        This article covers{' '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions
                        </strong>
                        , including lender and borrower information,{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Box 1 (Student Loan Interest Received by Lender)
                        </strong>{' '}
                        and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Box 2 (Checkbox)
                        </strong>
                        , which indicates whether the loan was originated before
                        September 1, 2004.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>{' '}
                        Late, incorrect, or incomplete filing of{' '}
                        <strong style={{ fontWeight: 600 }}>Form 1098-E</strong>{' '}
                        may result in IRS penalties ranging from{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on when the return is filed, whether
                        corrections are required, and whether the filer complies
                        with electronic filing requirements.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 1098-E, <b>Student Loan Interest Statement</b>, is an
                  information return submitted by a lender to report the
                  interest income received on the Qualified student loan
                  provided. This is the annual form that needs to be filed by
                  the lender if the lender receives more than $600 as interest
                  from the student loan provided.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file form 1098-E?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The financial institution, government agencies, educational
                  institution, or any other individual who receives more than
                  $600 in interest from a student loan is required to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-e/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1098-E
                  </Link>{' '}
                  with the IRS. Additionally, they must provide a copy to the
                  student who took out the loan. If there is more than one
                  individual is connected with the loan, the first individual to
                  receive the interest amount should file the form 1098-E.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Qualified Student loan
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  A qualified student loan is a loan certified by the student to
                  be used solely for educational expenses. It must be recognized
                  by the federal, state, or local government, or postsecondary
                  educational institution, or be at least subsidized,
                  guaranteed, or financed specifically for educational purposes.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Revolving accounts
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Interest paid on a revolving account such as a credit card
                  account for the sole purpose of educational expenses should be
                  reported under form 1098-E. This reporting is essential only
                  when the borrower certifies the amount is used only for the
                  qualified educational expenses. The lender need not verify the
                  student&apos;s actual spending of the money.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates for filing form 1098-E
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  File Form 1098-E before the deadline to avoid IRS penalties
                  and stay compliant with IRS tax standards.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Filing To IRS
                </Typography>
                <Box component="ol" sx={{ mb: 3.5, '& li': { mb: 1.5 } }}>
                  <li>
                    <b> E-Filing:</b> IRS encourages lenders to{' '}
                    <Link
                      href="/information-returns/efile-irs-form-1098-e/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      file form 1098-E
                    </Link>{' '}
                    using electronic filing methods for efficient and faster
                    filing and processing. E-filing is mandatory if the lender
                    needs to file more than 10 information returns in a tax
                    year.
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 31, 2026
                      </Typography>
                    </Box>
                  </li>
                  <li>
                    <b> Paper filing:</b> File form 1098-E using paper filing
                    only if you have to file less than 10 information returns to
                    be filed for the tax year.
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 2, 2026
                      </Typography>
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Sending Borrower copy
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  The lender should issue the borrower copy to the student
                  before the deadline to avoid penalties and compliance issues.
                  Lender filing using E-filing and paper filing methods both
                  need to send the borrower copy.
                </Typography>
                <Box className="deadline-dates" sx={{ mb: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      color: '#fff',
                      fontSize: 18,
                      fontWeight: 500,
                    }}
                  >
                    <b style={{ fontSize: 22 }}>
                      {' '}
                      Deadline for the Tax year 2025 :
                    </b>{' '}
                    February 2, 2026
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                  }}
                >
                  Step-by-step instructions to file form 1098-E
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Follow the steps to file form 1098-E
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Lenders Detail
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-e-recipient.webp" />
                  <source srcSet="/assets/images/1098-e-recipient.png" />
                  <img
                    src="/assets/images/1098-e-recipient.png"
                    loading="lazy"
                    alt="Step 1: Lenders Detail"
                    width={525}
                    height={208}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Name, address, and telephone number of the filer the
                  lender along with the lender/recipient Tax Identification
                  Number.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Borrower Details
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/1098-e-borrower-img.webp" />
                  <source srcSet="/assets/images/1098-e-borrower-img.png" />
                  <img
                    src="/assets/images/1098-e-borrower-img.png"
                    loading="lazy"
                    alt="Step 2: Borrower Details"
                    width={525}
                    height={286}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name of the borrower (here it is the student who
                  acquired the student loan), address, and Tax Identification
                  Number of the borrower. The account number is required if the
                  lender has multiple accounts with the borrower and needs to
                  file a separate 1098-E form for each account.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Student Loan interest details
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Student Loan Interest Received by Lender{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-e-student-img.webp" />
                  <source srcSet="/assets/images/1098-e-student-img.png" />
                  <img
                    src="/assets/images/1098-e-student-img.png"
                    loading="lazy"
                    alt="Box 1: Student Loan Interest Received by Lender"
                    width={432}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount of interest received from the student loan,
                  which includes capitalized interest and charges for the usage
                  or delay(forbearance) of the loan and the loan should be
                  originated on or after September 1, 2004.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Checkbox
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/1098-e-check-box-img.webp" />
                    <source srcSet="/assets/images/1098-e-check-box-img.png" />
                    <img
                      src="/assets/images/1098-e-check-box-img.png"
                      loading="lazy"
                      alt="Box 2: Checkbox"
                      width={432}
                      height={53}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check this box if there is a loan originated before September
                  1, 2004, and the capitalized interest or charges are not
                  reported in Box 1.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File form 1098-E With TaxZerone
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Affordable filing starts at <b>$2.49</b>/per form
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontSize: { lg: 18, md: 18, sm: 16, xs: 16 } }}
            >
              File in Bulk, Save more
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Filing per form can go down up to <b>$0.59</b>
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098einstructions`}
              >
                E-File Form 1098-E
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1098EInstructions;
